/**
 * Convai AI Configuration
 *
 * Configuration for the "Hope" AI companion that provides mental health support
 * between therapy sessions.
 *
 * Features:
 * - Diagnosis education in accessible language
 * - Coping strategies and quick tips
 * - Crisis detection and resource provision
 * - Compassionate, trauma-informed communication
 *
 * HIPAA Compliance:
 * - Convai does NOT receive PHI (Protected Health Information)
 * - Only generic conversation text is sent to Convai API
 * - Full conversation logs stored in Firebase (HIPAA-compliant with BAA)
 */

export interface ConvaiConfig {
  apiKey: string;
  characterId: string;
  enableAudio: boolean;
  enableFacialData: boolean;
  enableActions: boolean;
  sessionTimeout: number; // milliseconds
}

/**
 * Convai SDK Configuration
 *
 * API Key and Character ID are loaded from environment variables.
 * These should be set in your .env file:
 * - REACT_APP_CONVAI_API_KEY
 * - REACT_APP_CONVAI_CHARACTER_ID
 */
export const convaiConfig: ConvaiConfig = {
  // API credentials from Convai console
  apiKey: process.env.REACT_APP_CONVAI_API_KEY || '',
  characterId: process.env.REACT_APP_CONVAI_CHARACTER_ID || '',

  // Start with text-only (Phase 1: Simple & Supportive)
  enableAudio: false, // Will enable in Phase 2 for voice input/output
  enableFacialData: false, // Not needed for mental health support
  enableActions: false, // Not using character actions

  // Session timeout (15 minutes of inactivity)
  sessionTimeout: 15 * 60 * 1000,
};

/**
 * "Hope" Character Personality Guidelines
 *
 * These should be configured in the Convai console when creating the character:
 *
 * CHARACTER NAME: Hope
 *
 * PERSONALITY TRAITS:
 * - Warm, empathetic, and non-judgmental
 * - Patient and encouraging
 * - Trauma-informed and culturally sensitive
 * - Professional but approachable
 * - Uses simple, accessible language
 *
 * CONVERSATION STYLE:
 * - Short, digestible responses (2-4 sentences max)
 * - Validates feelings before offering suggestions
 * - Uses "I" statements ("I hear you", "I'm here with you")
 * - Avoids clinical jargon unless explaining diagnoses
 * - Offers choices rather than directives
 *
 * BOUNDARIES:
 * - NOT a replacement for therapy
 * - Cannot prescribe medication or provide medical advice
 * - Refers to provider for clinical decisions
 * - Escalates crisis situations to 988 Lifeline
 *
 * EXAMPLE BACKSTORY (for Convai console):
 *
 * "You are Hope, a compassionate mental health companion. Your purpose is to
 * support people between therapy sessions by helping them understand their
 * diagnoses, practice coping strategies, and feel less alone.
 *
 * You communicate with warmth and empathy, using simple language. You validate
 * feelings before offering suggestions. You know when to listen and when to
 * guide. You are NOT a therapist - you're a supportive companion who helps
 * people implement what they're learning in therapy.
 *
 * When someone is in crisis (mentions suicide, self-harm, or immediate danger),
 * you provide crisis resources immediately: 988 Suicide & Crisis Lifeline,
 * Crisis Text Line (HOME to 741741), and 911 for emergencies.
 *
 * You believe in people's capacity for growth and healing. You remind them of
 * their strengths while acknowledging their struggles. You are a source of
 * hope - your name reflects your purpose."
 */

/**
 * Crisis Detection Keywords
 *
 * These keywords trigger immediate crisis resource response.
 * Used in HopeAICompanion component for local detection.
 */
export const CRISIS_KEYWORDS = [
  // Suicide indicators
  'suicide',
  'kill myself',
  'end it all',
  'want to die',
  'better off dead',
  'no reason to live',
  'can\'t go on',

  // Self-harm indicators
  'hurt myself',
  'self-harm',
  'cutting',
  'self harm',
  'harm myself',

  // Immediate danger
  'overdose',
  'od',
  'end my life',
  'plan to die',
];

/**
 * Crisis Resources
 *
 * Displayed when crisis keywords are detected
 */
export const CRISIS_RESOURCES = {
  lifeline: {
    name: '988 Suicide & Crisis Lifeline',
    phone: '988',
    description: 'Call or text 988 - Available 24/7',
    url: 'https://988lifeline.org',
  },
  crisisTextLine: {
    name: 'Crisis Text Line',
    text: 'HOME to 741741',
    description: 'Text support available 24/7',
    url: 'https://www.crisistextline.org',
  },
  emergency: {
    name: 'Emergency Services',
    phone: '911',
    description: 'For immediate danger',
  },
};

/**
 * Conversation Storage Settings
 *
 * All conversations are stored in Firebase for:
 * - HIPAA compliance (with patient consent)
 * - Provider review capability
 * - Crisis event tracking
 * - Quality improvement
 */
export const CONVERSATION_STORAGE = {
  // Firebase collection name
  collectionName: 'ai_conversations',

  // Fields stored per message
  fields: {
    patientId: 'string',
    message: 'string',
    response: 'string',
    timestamp: 'timestamp',
    crisisDetected: 'boolean',
    sessionId: 'string',
  },

  // Retention policy (6 years for HIPAA compliance)
  retentionYears: 6,
};

/**
 * Validate Convai Configuration
 *
 * Checks if API key and Character ID are properly configured
 */
export const validateConvaiConfig = (): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!convaiConfig.apiKey || convaiConfig.apiKey === 'YOUR_CONVAI_API_KEY_HERE') {
    errors.push('REACT_APP_CONVAI_API_KEY not configured in .env file');
  }

  if (!convaiConfig.characterId || convaiConfig.characterId === 'YOUR_CHARACTER_ID_HERE') {
    errors.push('REACT_APP_CONVAI_CHARACTER_ID not configured in .env file');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};
